import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { Service, SourcesMap } from 'webapp-types';

import type ResolutionCenterItem from 'webapp/models-custom/resolutioncenter-item';
import Serviced from 'webapp/models-custom/serviced';
import type SourceType from 'webapp/models-custom/source-type';
import Template from 'webapp/models-custom/template';

import type { onConnectSourceResult } from 'webapp/services/connect';

export interface SourceData {
	sourceType: SourceType;
	connectedSourceId?: string | null;
	messageId?: number;
	isSolving?: boolean;
}

export default class ResolveSourcesData extends Serviced {
	@service('connect') declare connectService: Service.Connect;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;

	@tracked sourcesToConnect: SourceData[] = [];
	@tracked sourcesToChoose: SourceData[] = [];
	@tracked sourcesMap: SourcesMap = {};

	private objectToResolve: ResolutionCenterItem | Template;
	private sourceTypeIdsToResolve: number[] = [];

	constructor(objectToResolve: Template | ResolutionCenterItem, sourcesMap?: SourcesMap) {
		super();

		this.objectToResolve = objectToResolve;
		this.sourceTypeIdsToResolve =
			objectToResolve instanceof Template
				? objectToResolve.sources
				: objectToResolve.params.dataSourceTypeIds!; // set on messages with sources to resolve

		this.sourcesMap = sourcesMap || {};

		this.updateSourcesData();
	}

	private get sourceTypeIdsToResolveCount(): number {
		if (!this.objectToResolve) {
			return -1;
		}

		const sourcesToConnect = this.sourcesToConnect.filter((item) => !item.connectedSourceId),
			sourcesToChoose = this.sourcesToChoose.filter((item) => !item.connectedSourceId);

		return sourcesToConnect.length + sourcesToChoose.length;
	}

	get hasOnlyConnect(): boolean {
		return !this.sourcesToChoose.length;
	}

	get hasOnlyChoose(): boolean {
		return !this.sourcesToConnect.length;
	}

	get hasMultipleSources(): boolean {
		return this.sourceTypeIdsToResolveCount > 1;
	}

	get isAnyResolved(): boolean {
		return Object.keys(this.sourcesMap).length > 0;
	}

	get isAllResolved(): boolean {
		return this.sourceTypeIdsToResolveCount === 0 && this.isAnyResolved;
	}

	private updateSourcesData() {
		const connectedSources = this.mmSourcesService.sourceModels,
			sourcesToConnect: SourceData[] = [],
			sourcesToChoose: SourceData[] = [];

		this.sourceTypeIdsToResolve.forEach((sourceTypeId) => {
			const sourceType = this.mmSourceTypesService.findModelById(sourceTypeId)!,
				connectedSourcesOfType = connectedSources.filter(
					(s) => s.sourceType.id === sourceTypeId,
				),
				sourceData: SourceData = {
					sourceType,
					connectedSourceId: this.sourcesMap[sourceTypeId],
				};

			if (connectedSourcesOfType.length === 0) {
				// don't have any of type
				sourcesToConnect.push(sourceData);
			} else {
				// let user choose (or connect new)
				sourcesToChoose.push(sourceData);
			}
		});

		this.sourcesToConnect = [...sourcesToConnect];
		this.sourcesToChoose = [...sourcesToChoose];
	}

	resolve(sourceTypeId: number, matchedSourceId: string | null, isSuccess: boolean): void {
		if (!isSuccess) {
			return;
		}

		const sourceToChoose = this.sourcesToChoose.find((s) => s.sourceType.id === sourceTypeId),
			sourceToConnect = this.sourcesToConnect.find((s) => s.sourceType.id === sourceTypeId);

		if (sourceToChoose) {
			sourceToChoose.connectedSourceId = matchedSourceId;
		}

		if (sourceToConnect) {
			sourceToConnect.connectedSourceId = matchedSourceId;
		}

		if (matchedSourceId) {
			this.sourcesMap[sourceTypeId] = matchedSourceId;
		} else {
			delete this.sourcesMap[sourceTypeId];
		}

		this.updateSourcesData();
	}

	connect(sourceData: SourceData, origin: string): Promise<void> {
		const sourceType = sourceData.sourceType;

		return new Promise((resolve) => {
			void this.connectService
				.connectSourceFromIdOrKey(sourceType.key, { origin, isUseTemplate: true })
				.then((reply: onConnectSourceResult) => {
					if (reply.isSuccess) {
						const source = reply.source;
						this.resolve(sourceType.id!, String(source.id), true);
					} else {
						const errorText = reply.error;
						this.infolayerService.error(errorText);
					}

					resolve();
				});
		});
	}
}
